import React, { useState } from 'react';
import { 
    Layout, 
    Card, 
    Row, 
    Col, 
    Table, 
    Tag, 
    Button, 
    Space, 
    Select, 
    DatePicker, 
    message, 
    Typography 
} from 'antd';
import { 
    SearchOutlined, 
    ClearOutlined, 
    FileExcelOutlined, 
    ArrowLeftOutlined,
    TransactionOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Content } = Layout;
const { RangePicker } = DatePicker;

const PaymentDetailsReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({
        payment_type: '',
        dateRange: [null, null]
    });

    const columns = [
        {
            title: 'Invoice No',
            dataIndex: 'inv_ref',
            key: 'inv_ref',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Invoice Date',
            dataIndex: 'invoice_dt',
            key: 'invoice_dt',
            render: (date) => date ? dayjs(date).format('DD MMM YYYY') : 'N/A'
        },
        {
            title: 'Payment Type',
            dataIndex: 'payment_type',
            key: 'payment_type',
            render: (type) => (
                <Tag color={type === 'CASH' ? 'green' : type === 'CARD' ? 'blue' : 'purple'}>
                    {type}
                </Tag>
            )
        },
        {
            title: 'Payment Reference',
            dataIndex: 'payment_ref',
            key: 'payment_ref',
            render: (ref) => ref || '-'
        },
        {
            title: 'Payment Amount',
            dataIndex: 'paid_amount',
            key: 'paid_amount',
            align: 'right',
            render: (amount) => `Rs. ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        },
        {
            title: 'Payment Date Time',
            dataIndex: 'payment_dt',
            key: 'payment_dt',
            render: (date) => date ? dayjs(date).format('DD MMM YYYY hh:mm A') : 'N/A'
        }
    ];

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.payment_type) params.append('payment_type', filters.payment_type);
            
            if (filters.dateRange[0] && filters.dateRange[1]) {
                params.append('start_date', filters.dateRange[0].format('YYYY-MM-DD'));
                params.append('end_date', filters.dateRange[1].format('YYYY-MM-DD'));
            }

            const response = await axiosClient.get(`/reports/get_payment_details_report.php?${params.toString()}`);
            if (response.data.status === 'success') {
                setData(response.data.data || []);
                if (response.data.data.length === 0) {
                    message.info('No matching payments found for the selected criteria.');
                }
            }
        } catch (error) {
            console.error('Fetch report error:', error);
            message.error('Failed to fetch payment details report.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFilters({
            payment_type: '',
            dateRange: [null, null]
        });
        setData([]);
    };

    const exportToExcel = () => {
        if (data.length === 0) return;
        
        const worksheetData = data.map(item => ({
            'Invoice No': item.inv_ref,
            'Invoice Date': item.invoice_dt ? dayjs(item.invoice_dt).format('DD MMM YYYY') : 'N/A',
            'Payment Type': item.payment_type,
            'Payment Reference': item.payment_ref,
            'Payment Amount': Number(item.paid_amount),
            'Payment Date Time': item.payment_dt ? dayjs(item.payment_dt).format('DD MMM YYYY hh:mm A') : 'N/A'
        }));

        const ws = XLSX.utils.json_to_sheet(worksheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Payment Details");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const finalData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(finalData, `Payment_Details_Report_${dayjs().format('YYYYMMDD')}.xlsx`);
    };

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Headerdiv />
            <Content style={{ padding: '24px' }}>
                <div style={{ marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                {/* Filter Card */}
                <Card 
                    title={
                        <Space>
                            <TransactionOutlined />
                            <span>Payment Details Report</span>
                        </Space>
                    }
                    extra={
                        <Space wrap>
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />} 
                                onClick={fetchReport} 
                                loading={loading}
                            >
                                Generate
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClear}>Clear</Button>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Payment Type</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="All Payment Types"
                                value={filters.payment_type}
                                onChange={(val) => setFilters({...filters, payment_type: val})}
                                allowClear
                            >
                                <Select.Option value="">All Types</Select.Option>
                                <Select.Option value="CASH">Cash</Select.Option>
                                <Select.Option value="CARD">Card</Select.Option>
                                <Select.Option value="TRANSFER">Bank Transfer</Select.Option>
                                <Select.Option value="OTHER">Other</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Payment Date Range</div>
                            <RangePicker 
                                style={{ width: '100%' }} 
                                value={filters.dateRange}
                                onChange={(dates) => setFilters({...filters, dateRange: dates})}
                            />
                        </Col>
                    </Row>
                </Card>

                {/* Results Card */}
                <Card 
                    title={`Report Results ${data.length > 0 ? `(${data.length} transactions)` : ''}`}
                    extra={
                        data.length > 0 && (
                            <Button 
                                icon={<FileExcelOutlined />} 
                                onClick={exportToExcel}
                                style={{ backgroundColor: '#217346', color: '#fff', borderColor: '#217346' }}
                            >
                                Export to Excel
                            </Button>
                        )
                    }
                >
                    <Table 
                        dataSource={data}
                        columns={columns}
                        rowKey="inv_pay_id"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                        }}
                        bordered
                        size="small"
                        locale={{ emptyText: 'No data. Adjust filters and click Generate.' }}
                        scroll={{ x: true }}
                    />
                </Card>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default PaymentDetailsReport;
